import React, { useMemo, useState } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableAction, TableColumn } from "@shared/types/table";
import { Switch } from "@shared/ui/switch";
import { Check, X, Plus } from "lucide-react";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { ResourceTypeForm } from "../create/create";
import { DeleteConfirmationDialog } from "@shared/components/DeleteConfirmationDialog";
import { ResourceTypeDetailDialog } from "../detail/Detail";
import { PageHeader } from "@shared/ui/pageHeader";
import { useGetResourceTypeList } from "../../../../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { PAGE_SIZE } from "@shared/utils/constants";


interface ResourceType {
  id: string;
  name: string;
  description: string;
  activities: string;
  cycles: boolean;
  routes: boolean;
  validation: boolean;
  inventory: boolean;
  svcOrders: boolean;
  status: boolean;
}

const mockResourceTypes: ResourceType[] = [
  {
    id: "1",
    name: "Water meter",
    description: "Standard residential water meter resource type",
    activities: "Read, replace, inspect",
    cycles: true,
    routes: true,
    validation: true,
    inventory: false,
    svcOrders: true,
    status: true,
  },
  {
    id: "2",
    name: "Gas meter",
    description: "Industrial gas meter resource type with pressure validation",
    activities: "Read, calibrate, service",
    cycles: false,
    routes: true,
    validation: false,
    inventory: true,
    svcOrders: false,
    status: false,
  },
  {
    id: "3",
    name: "Electricity meter",
    description: "Smart grid electrical meters with cellular telemetry",
    activities: "Remote read, disconnect",
    cycles: true,
    routes: false,
    validation: true,
    inventory: true,
    svcOrders: true,
    status: true,
  },
];

export function ResourceTypeList() {
  const remoteUtilityId = useRemoteUtilityId();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const { data: resourceTypesResponse, isLoading } = useGetResourceTypeList(remoteUtilityId, currentPage, PAGE_SIZE, searchQuery);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editData, setEditData] = useState<ResourceType | undefined>(undefined);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState<ResourceType | undefined>(undefined);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [viewData, setViewData] = useState<ResourceType | undefined>(undefined);

  const handleStatusChange = (id: string, checked: boolean) => {
    console.log("Status change:", id, checked);
  };

  const renderBooleanCell = (value: any) => {
    console.log('yessssss',value)
    if (value === 1) {
      return (
        <div className="flex items-center gap-1 text-green-600 font-medium">
          <Check className="w-4 h-4 text-green-500" />
          <span>Yes</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 text-red-600 font-medium">
        <X className="w-4 h-4 text-red-500" />
        <span>No</span>
      </div>
    );
  };

  const columns: TableColumn<ResourceType>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        type: "text",
        render: (value) => <span className="font-medium text-gray-900">{value}</span>,
      },
      {
        key: "description",
        header: "Description",
        type: "text",
        render: (value) => <span className="text-gray-600">{value}</span>,
      },
      {
        key: "cycles",
        header: "Capture Reads",
        type: "custom",
        render: (_, row: any) => renderBooleanCell(row?.capabilities?.captureReads),
      },
      {
        key: "svcOrders",
        header: "Service Orders",
        type: "custom",
        render: (_, row: any) => renderBooleanCell(row?.capabilities?.supportsServiceOrders),
      },
    ],
    []
  );

  const handleView = (row: ResourceType) => {
    setViewData(row);
    setIsDetailOpen(true);
  };

  const handleEdit = (row: ResourceType) => {
    setEditData(row);
    setIsFormOpen(true);
  };

  const handleDelete = (row: ResourceType) => {
    setDeleteItem(row);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    console.log("Deleted resource type:", deleteItem?.id);
    setIsDeleteOpen(false);
    setDeleteItem(undefined);
  };

  const actions: TableAction[] = [
    {
      label: "Edit",
      onClick: handleEdit,
      icon: "Pencil",
      variant: "outline",
    },
    {
      label: "Delete",
      onClick: handleDelete,
      icon: "Trash2",
      variant: "outline",  
      disabled: () => true

    },
  ];

  const tableData = resourceTypesResponse?.result ?? [];
  const totalItems = resourceTypesResponse?.count ?? tableData.length;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages: Math.ceil(totalItems / PAGE_SIZE) || 1,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalItems]
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <PageHeader
          title="Resource Types"
          description="Manage and configure resource types and their capabilities"
        />
        <ResourceTypeForm
          trigger={
            <Button className="flex items-center gap-1">
              <Plus className="w-4.5 h-4.5" />
              <span>Add Resource Type</span>
            </Button>
          }
        />
      </div>
      <GenericTable
        data={tableData}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        actions={actions}
        actionsType="icons"
        pagination={pagination}
        onPageChange={(page) => setCurrentPage(page)}
        emptyMessage="No resource types found"
        onSearch={(val) => {
          setSearchQuery(val);
          setCurrentPage(1);
        }}
        searchPlaceholder="Search..."
      />

      <ResourceTypeForm
        open={isFormOpen}
        onOpenChange={(open) => {
          setIsFormOpen(open);
          if (!open) setEditData(undefined);
        }}
        editData={editData}
      />

      <DeleteConfirmationDialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteItem(undefined);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete resource type"
        itemName={deleteItem?.name}
      />

      <ResourceTypeDetailDialog
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setViewData(undefined);
        }}
        data={viewData}
      />
    </div>
  );
}
